import { YammerCache } from './types';

interface CacheItem {
  readonly value: unknown;
  readonly expiry: number;
  readonly ttl: number;
}

export default class CacheWithExpiration implements YammerCache {
  private storage: YammerCache;
  private keyNamespace?: string;

  constructor(storage: YammerCache, keyNamespace?: string) {
    this.storage = storage;
    this.keyNamespace = keyNamespace;
  }

  public getItem<T>(key: string): T | null {
    const keyWithNamespace = this.getKeyWithNamespace(key);
    const cachedItem = this.storage.getItem<CacheItem>(keyWithNamespace);

    if (!cachedItem) {
      return null;
    }

    const { value, expiry } = cachedItem;

    if (expiry > 0 && expiry < Date.now()) {
      this.removeItem(keyWithNamespace);

      return null;
    }

    return value as T;
  }

  public getTimeInCache(key: string): number | null {
    const keyWithNamespace = this.getKeyWithNamespace(key);
    const cachedItem = this.storage.getItem<CacheItem>(keyWithNamespace);

    if (!cachedItem) {
      return null;
    }

    const { expiry, ttl } = cachedItem;

    const hasTTL = !!ttl;
    const isExpired = expiry > 0 && expiry < Date.now();

    if (!hasTTL || isExpired) {
      return null;
    }

    return Date.now() - (expiry - ttl);
  }

  public setItem<T>(key: string, value: T, ttlInSeconds?: number): void {
    const keyWithNamespace = this.getKeyWithNamespace(key);
    const ttl = ttlInSeconds ? ttlInSeconds * 1000 : 0;
    const expiry = ttlInSeconds ? Date.now() + ttl : 0;
    this.storage.setItem(keyWithNamespace, { value, expiry, ttl });
  }

  public removeItem(key: string): void {
    const keyWithNamespace = this.getKeyWithNamespace(key);
    this.storage.removeItem(keyWithNamespace);
  }

  public clear(): void {
    this.storage.clear();
  }

  private getKeyWithNamespace(key: string): string {
    return this.keyNamespace ? `${this.keyNamespace}::${key}` : key;
  }
}
